(() => {
  const SECTION_SELECTOR = '[data-landing-hsn]';

  function initStickyCta(section) {
    if (section.dataset.stickyEnabled !== 'true') return;

    const sticky = section.querySelector('[data-landing-hsn-sticky]');
    if (!(sticky instanceof HTMLElement)) return;

    const threshold =
      Number.parseInt(section.dataset.stickyThreshold || '600', 10) || 0;
    const buySection = section.querySelector('[data-landing-hsn-buy]');

    let ticking = false;
    let visible = false;

    const setVisible = (nextVisible) => {
      if (nextVisible === visible) return;

      visible = nextVisible;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const updateVisibility = () => {
      const buySectionRect =
        buySection instanceof HTMLElement
          ? buySection.getBoundingClientRect()
          : null;
      const buySectionVisible =
        buySectionRect !== null &&
        buySectionRect.top < window.innerHeight - 72 &&
        buySectionRect.bottom > 72;

      setVisible(
        window.scrollY >= threshold &&
        !buySectionVisible
      );
    };

    const onScroll = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(() => {
        updateVisibility();
        ticking = false;
      });
    };

    updateVisibility();

    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll, {
      passive: true
    });

    section._landingHsnCleanup =
      section._landingHsnCleanup || [];

    section._landingHsnCleanup.push(() => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    });
  }

  function initTabs(section) {
    const root = section.querySelector('[data-landing-hsn-tabs]');
    if (!(root instanceof HTMLElement)) return;

    const tabs = Array.from(
      root.querySelectorAll('[role="tab"]')
    );

    const panels = Array.from(
      root.querySelectorAll('[data-tab-panel]')
    );

    if (!tabs.length || !panels.length) return;

    const activate = (tabId) => {
      tabs.forEach((tab) => {
        const isActive =
          tab.getAttribute('data-tab') === tabId;

        tab.classList.toggle('is-active', isActive);

        tab.setAttribute(
          'aria-selected',
          isActive ? 'true' : 'false'
        );

        tab.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        const isActive =
          panel.getAttribute('data-tab-panel') === tabId;

        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    };

    const onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');

      if (
        !(tab instanceof HTMLElement) ||
        !root.contains(tab)
      ) {
        return;
      }

      activate(tab.getAttribute('data-tab'));
    };

    const onKeydown = (event) => {
      const current =
        event.target.closest('[role="tab"]');

      if (
        !(current instanceof HTMLElement) ||
        !root.contains(current)
      ) {
        return;
      }

      const index = tabs.indexOf(current);

      if (index < 0) return;

      let nextIndex = index;

      if (
        event.key === 'ArrowRight' ||
        event.key === 'ArrowDown'
      ) {
        nextIndex = (index + 1) % tabs.length;
      } else if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowUp'
      ) {
        nextIndex =
          (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();

      tabs[nextIndex].focus();

      activate(
        tabs[nextIndex].getAttribute('data-tab')
      );
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);

    section._landingHsnCleanup =
      section._landingHsnCleanup || [];

    section._landingHsnCleanup.push(() => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeydown);
    });
  }

  function initPlans(section) {
    const root = section.querySelector(
      '[data-landing-hsn-plans]'
    );

    if (!(root instanceof HTMLElement)) return;

    const plans = Array.from(
      root.querySelectorAll('[data-plan]')
    );

    const cta = section.querySelector(
      '[data-landing-hsn-buy-cta]'
    );

    if (!plans.length) return;

    const selectPlan = (planButton) => {
      plans.forEach((plan) => {
        const isSelected = plan === planButton;

        plan.classList.toggle(
          'is-selected',
          isSelected
        );

        plan.setAttribute(
          'aria-checked',
          isSelected ? 'true' : 'false'
        );
      });

      if (
        cta instanceof HTMLElement &&
        !cta.hasAttribute('disabled')
      ) {
        const label =
          planButton.getAttribute('data-cta-label');

        const labelNode = cta.querySelector('span');

        if (label && labelNode) {
          labelNode.textContent = label;
        }
      }
    };

    const onClick = (event) => {
      const plan = event.target.closest('[data-plan]');

      if (
        !(plan instanceof HTMLElement) ||
        !root.contains(plan)
      ) {
        return;
      }

      selectPlan(plan);
    };

    root.addEventListener('click', onClick);

    section._landingHsnCleanup =
      section._landingHsnCleanup || [];

    section._landingHsnCleanup.push(() => {
      root.removeEventListener('click', onClick);
    });
  }

  function initMarquee(section) {
    const marquee = section.querySelector(
      '[data-landing-hsn-marquee]'
    );

    if (!(marquee instanceof HTMLElement)) return;

    const track = marquee.querySelector(
      '.landing-hsn__marquee-track'
    );

    if (!(track instanceof HTMLElement)) return;

    if (
      track.dataset.marqueeInitialised === 'true'
    ) {
      return;
    }

    const sourceItems = Array.from(track.children);

    if (!sourceItems.length) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    const pixelsPerSecond = 45;
    const minimumDuration = 12000;

    let animation = null;
    let resizeFrame = null;

    track.dataset.marqueeInitialised = 'true';

    const createClone = (item) => {
      const clone = item.cloneNode(true);

      clone.setAttribute('aria-hidden', 'true');

      clone.dataset.marqueeGenerated = 'true';

      return clone;
    };

    const clearGeneratedItems = () => {
      track
        .querySelectorAll('[data-marquee-generated]')
        .forEach((item) => item.remove());
    };

    const stopAnimation = () => {
      if (animation) {
        animation.cancel();
        animation = null;
      }

      track.style.removeProperty('transform');
      track.style.animation = 'none';
    };

    const rebuild = () => {
      stopAnimation();
      clearGeneratedItems();

      /*
       * Build one logical group that is wider than
       * the visible marquee. This prevents large
       * screens revealing empty space.
       */
      const minimumGroupWidth =
        marquee.clientWidth + 100;

      let repetitions = 1;

      while (
        track.scrollWidth < minimumGroupWidth &&
        repetitions < 20
      ) {
        sourceItems.forEach((item) => {
          track.appendChild(createClone(item));
        });

        repetitions += 1;
      }

      const firstGroup =
        Array.from(track.children);

      const firstGroupLength =
        firstGroup.length;

      /*
       * Duplicate the completed group once.
       * The animation travels from the first
       * group to an identical second group,
       * making the loop reset seamless.
       */
      firstGroup.forEach((item) => {
        track.appendChild(createClone(item));
      });

      const firstItem = track.children[0];

      const secondGroupStart =
        track.children[firstGroupLength];

      if (
        !(firstItem instanceof HTMLElement) ||
        !(secondGroupStart instanceof HTMLElement)
      ) {
        return;
      }

      const distance =
        secondGroupStart.offsetLeft -
        firstItem.offsetLeft;

      if (
        distance <= 0 ||
        reduceMotion.matches
      ) {
        return;
      }

      const duration = Math.max(
        minimumDuration,
        Math.round(
          (distance / pixelsPerSecond) * 1000
        )
      );

      animation = track.animate(
        [
          {
            transform:
              'translate3d(0, 0, 0)'
          },
          {
            transform:
              `translate3d(-${distance}px, 0, 0)`
          }
        ],
        {
          duration,
          iterations: Infinity,
          easing: 'linear'
        }
      );
    };

    const scheduleRebuild = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      resizeFrame =
        window.requestAnimationFrame(() => {
          rebuild();
          resizeFrame = null;
        });
    };

    const onMotionPreferenceChange = () => {
      rebuild();
    };

    rebuild();

    window.addEventListener(
      'resize',
      scheduleRebuild,
      {
        passive: true
      }
    );

    if (
      typeof reduceMotion.addEventListener ===
      'function'
    ) {
      reduceMotion.addEventListener(
        'change',
        onMotionPreferenceChange
      );
    } else if (
      typeof reduceMotion.addListener ===
      'function'
    ) {
      reduceMotion.addListener(
        onMotionPreferenceChange
      );
    }

    section._landingHsnCleanup =
      section._landingHsnCleanup || [];

    section._landingHsnCleanup.push(() => {
      window.removeEventListener(
        'resize',
        scheduleRebuild
      );

      if (
        typeof reduceMotion.removeEventListener ===
        'function'
      ) {
        reduceMotion.removeEventListener(
          'change',
          onMotionPreferenceChange
        );
      } else if (
        typeof reduceMotion.removeListener ===
        'function'
      ) {
        reduceMotion.removeListener(
          onMotionPreferenceChange
        );
      }

      if (resizeFrame !== null) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      stopAnimation();
      clearGeneratedItems();

      delete track.dataset.marqueeInitialised;
    });
  }

  function cleanupLandingSection(section) {
    if (!(section instanceof HTMLElement)) return;

    const cleanups =
      section._landingHsnCleanup;

    if (Array.isArray(cleanups)) {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (error) {
          /* noop */
        }
      });
    }

    section._landingHsnCleanup = [];

    delete section.dataset
      .landingHsnInitialised;
  }

  function initLandingSection(section) {
    if (!(section instanceof HTMLElement)) return;

    if (
      section.dataset.landingHsnInitialised ===
      'true'
    ) {
      return;
    }

    section.dataset.landingHsnInitialised =
      'true';

    section._landingHsnCleanup = [];

    initStickyCta(section);
    initTabs(section);
    initPlans(section);
    initMarquee(section);
    initReviewsCarousel(section);
  }

  function initReviewsCarousel(section) {
    const carousel = section.querySelector(
      '[data-landing-hsn-reviews-carousel]'
    );

    if (!(carousel instanceof HTMLElement)) return;
    if (carousel.dataset.reviewsInitialised === 'true') return;

    const prev = section.querySelector(
      '[data-landing-hsn-reviews-prev]'
    );
    const next = section.querySelector(
      '[data-landing-hsn-reviews-next]'
    );

    let attempts = 0;
    const maxAttempts = 40;

    const tryInit = () => {
      const $ = window.jQuery || window.$;

      if (!$ || typeof $.fn === 'undefined' || typeof $.fn.slick !== 'function') {
        attempts += 1;

        if (attempts < maxAttempts) {
          window.setTimeout(tryInit, 100);
        } else {
          carousel.classList.add('is-ready', 'is-static');
        }

        return;
      }

      const $carousel = $(carousel);

      if ($carousel.hasClass('slick-initialized')) {
        carousel.dataset.reviewsInitialised = 'true';
        $carousel.addClass('is-ready');
        return;
      }

      carousel.dataset.reviewsInitialised = 'true';

      $carousel.on('init', function () {
        $carousel.addClass('is-ready');
      });

      $carousel.slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        arrows: false,
        dots: false,
        speed: 500,
        cssEase: 'ease-in-out',
        adaptiveHeight: false,
        responsive: [
          {
            breakpoint: 992,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 1
            }
          },
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 1.5,
              slidesToScroll: 1
            }
          },
          {
            breakpoint: 580,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1
            }
          }
        ]
      });

      const onPrev = (event) => {
        event.preventDefault();
        $carousel.slick('slickPrev');
      };

      const onNext = (event) => {
        event.preventDefault();
        $carousel.slick('slickNext');
      };

      if (prev instanceof HTMLElement) {
        prev.addEventListener('click', onPrev);
      }

      if (next instanceof HTMLElement) {
        next.addEventListener('click', onNext);
      }

      section._landingHsnCleanup =
        section._landingHsnCleanup || [];

      section._landingHsnCleanup.push(() => {
        if (prev instanceof HTMLElement) {
          prev.removeEventListener('click', onPrev);
        }

        if (next instanceof HTMLElement) {
          next.removeEventListener('click', onNext);
        }

        if ($carousel.hasClass('slick-initialized')) {
          $carousel.slick('unslick');
        }

        delete carousel.dataset.reviewsInitialised;
      });
    };

    tryInit();
  }

  function initAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initLandingSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initLandingSection);
  }

  function unloadAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      cleanupLandingSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(cleanupLandingSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      unloadAll(event.target);
    }
  );
})();
